import { BufferAttribute, BufferGeometry } from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'

/**
 * Weld an imported triangle-soup geometry (STL/OBJ, where every triangle stores its three
 * corners in full) into an INDEXED geometry, so each unique vertex position is stored once.
 * This is done as early as possible after parsing: indexed geometry is ~2× lighter in memory
 * and on the wire, and it is what the slicer reconstructs triangles from.
 *
 * Already-indexed geometry (STEP/3MF) is returned untouched. The 1e-4 weld tolerance matches
 * the vertex precision the connected-component split uses, so welding never changes topology.
 */
export function toIndexedGeometry(geometry: BufferGeometry): BufferGeometry {
  if (geometry.index) {
    return geometry
  }
  // Weld by POSITION only — keep just the `position` attribute before merging. STL/OBJ carry
  // per-face normals (and STL can carry a per-face color): a vertex shared by two faces then
  // has different normals/colors, and mergeVertices merges only vertices whose attributes ALL
  // match, so leaving them in place would stop shared vertices from merging — the mesh would
  // stay a disconnected soup and split into one component per facet (a cylinder exploded into
  // hundreds of pieces). These attributes are also unused downstream: normals are recomputed,
  // and colors are never rendered (no material uses vertex colours) nor read by the slicer.
  for (const name of Object.keys(geometry.attributes)) {
    if (name !== 'position') {
      geometry.deleteAttribute(name)
    }
  }
  return BufferGeometryUtils.mergeVertices(geometry)
}

/**
 * Split a geometry into its connected components — one geometry per set of triangles reachable
 * through shared vertices. Replaces the old non-indexed union-find (which matched triangle
 * corners by rounded-coordinate string keys): once the geometry is indexed, two triangles are
 * connected iff they share a vertex id, so connectivity comes straight from the index.
 *
 * Input is indexed first if needed; every output component is itself an indexed geometry with a
 * compacted vertex buffer. A single-component geometry is returned as-is.
 */
export function splitConnectedComponents(geometry: BufferGeometry): BufferGeometry[] {
  const indexed = toIndexedGeometry(geometry)
  const index = indexed.index
  if (!index) {
    return [indexed]
  }

  const triangleCount = index.count / 3
  if (triangleCount <= 1) {
    return [indexed]
  }

  // Union-Find (path compression + union by rank) over triangles.
  const parent = new Int32Array(triangleCount)
  const rank = new Uint8Array(triangleCount)
  for (let i = 0; i < triangleCount; i++) {
    parent[i] = i
  }

  const find = (x: number): number => {
    let root = x
    while (parent[root] !== root) {
      parent[root] = parent[parent[root]] // path halving
      root = parent[root]
    }
    return root
  }

  const union = (a: number, b: number): void => {
    const ra = find(a)
    const rb = find(b)
    if (ra === rb) return
    if (rank[ra] < rank[rb]) {
      parent[ra] = rb
    }
    else if (rank[ra] > rank[rb]) {
      parent[rb] = ra
    }
    else {
      parent[rb] = ra
      rank[ra]++
    }
  }

  // Connect any two triangles that reference the same vertex id.
  const vertexToTriangle = new Map<number, number>()
  for (let t = 0; t < triangleCount; t++) {
    for (let k = 0; k < 3; k++) {
      const vertexId = index.getX(t * 3 + k)
      const existing = vertexToTriangle.get(vertexId)
      if (existing !== undefined) {
        union(t, existing)
      }
      else {
        vertexToTriangle.set(vertexId, t)
      }
    }
  }
  vertexToTriangle.clear()

  const components = new Map<number, number[]>()
  for (let t = 0; t < triangleCount; t++) {
    const root = find(t)
    let group = components.get(root)
    if (!group) {
      group = []
      components.set(root, group)
    }
    group.push(t)
  }

  if (components.size === 1) {
    return [indexed]
  }

  // Rebuild each component as an indexed geometry, remapping only the vertices it uses.
  const position = indexed.getAttribute('position')
  const output: BufferGeometry[] = []
  for (const triangles of components.values()) {
    const remap = new Map<number, number>()
    const positions: number[] = []
    const indices: number[] = []
    for (const t of triangles) {
      for (let k = 0; k < 3; k++) {
        const vertexId = index.getX(t * 3 + k)
        let mapped = remap.get(vertexId)
        if (mapped === undefined) {
          mapped = remap.size
          remap.set(vertexId, mapped)
          positions.push(position.getX(vertexId), position.getY(vertexId), position.getZ(vertexId))
        }
        indices.push(mapped)
      }
    }
    const component = new BufferGeometry()
    component.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3))
    component.setIndex(indices) // three picks Uint16/Uint32 based on vertex count
    output.push(component)
  }

  return output
}
